import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MomentDateAdapter,
} from '@angular/material-moment-adapter';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { BeneficiaryInfoDetailsDto } from '../../../../../modules/beneficiaries/dtos/beneficiary-info-details';
import { BeneficiaryPolicyDto } from '../../../../../modules/beneficiaries/dtos/beneficiary-policy';
import { PT_BR_DATE_FORMATS } from '../../../../../utils/date-formats.utils';
import { UpdateBeneficiaryPoliciesRequest } from '../../../models/beneficiaries.model';
import {
	editBeneficiaryPoliciesClear,
	editBeneficiaryPoliciesStart,
	fetchBeneficiaryAllPoliciesStart,
	restoreFormReset,
} from '../../../state/organizations.actions';
import {
	selectBeneficiaryAllPolicies,
	selectEditPoliciesModalResult,
	selectRestoreForm,
} from '../../../state/organizations.selector';

@Component({
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
		{
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
			provide: DateAdapter,
			useClass: MomentDateAdapter,
		},
		{ provide: MAT_DATE_FORMATS, useValue: PT_BR_DATE_FORMATS },
	],
	selector: 'app-edit-policies-modal',
	styles: [
		`
			.col-1 {
				width: 98%;
				min-width: 98%;
			}
			.gray {
				color: #626262;
			}
			@media only screen and (max-width: 1024px) {
				.line {
					flex-direction: column;
					align-items: flex-start;
				}
				.col-1 {
					width: 95%;
					min-width: 95%;
				}
			}
		`,
	],
	templateUrl: './edit-policies-modal.component.html',
})
export class EditPoliciesModalComponent implements OnInit, OnDestroy {
	dentalPolicies: BeneficiaryInfoDetailsDto[] = [
		{ id: 'NAO_APLICAVEL', name: 'Política não aplicável' },
	];

	form: UntypedFormGroup;
	healthPolicies: BeneficiaryInfoDetailsDto[] = [
		{ id: 'NAO_APLICAVEL', name: 'Política não aplicável' },
	];

	inputId: string = String(Math.random());
	loading = false;
	nonAplic: BeneficiaryInfoDetailsDto[] = [
		{ id: 'NAO_APLICAVEL', name: 'Política não aplicável' },
	];

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly store: Store,
		public dialogRef: MatDialogRef<EditPoliciesModalComponent>,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			beneficiaryId: string;
			dental: BeneficiaryPolicyDto | null;
			health: BeneficiaryPolicyDto | null;
			orgId: string;
			positionId: string;
		},
	) {
		this.form = this.fb.group({
			dental_policy: [
				this.data.dental?.not_aplicable
					? 'NAO_APLICAVEL'
					: this.data.dental?.policy_id,
				[Validators.required],
			],
			health_policy: [
				this.data.health?.not_aplicable
					? 'NAO_APLICAVEL'
					: this.data.health?.policy_id,
				[Validators.required],
			],
		});
	}

	closeModal() {
		this.dialogRef.close();
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(restoreFormReset());
	}

	ngOnInit(): void {
		this.store
			.select(selectBeneficiaryAllPolicies)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((ap) => {
				if (ap) {
					this.healthPolicies = this.nonAplic.concat(ap.healths);
					this.dentalPolicies = this.nonAplic.concat(ap.teeths);
					this.loading = false;
				}
			});
		this.store.dispatch(
			fetchBeneficiaryAllPoliciesStart({
				id: this.data.orgId,
				positionId: this.data.positionId,
			}),
		);
	}

	onContinue(): void {
		if (this.form.valid) {
			const form = this.form.value;
			const request: UpdateBeneficiaryPoliciesRequest = {
				policy_health_aplicable: form.health_policy === 'NAO_APLICAVEL',
				policy_health_id:
					form.health_policy === 'NAO_APLICAVEL' ? null : form.health_policy,
				policy_teeth_aplicable: form.dental_policy === 'NAO_APLICAVEL',
				policy_teeth_id:
					form.dental_policy === 'NAO_APLICAVEL' ? null : form.dental_policy,
			};
			this.store
				.select(selectRestoreForm)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((restore) => {
					if (restore) {
						this.updateValidation();
						this.loading = false;
					}
				});
			this.store
				.select(selectEditPoliciesModalResult)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((success) => {
					if (success) {
						this.store.dispatch(editBeneficiaryPoliciesClear());
						this.dialogRef.close(true);
					}
				});
			this.loading = true;
			this.store.dispatch(
				editBeneficiaryPoliciesStart({
					benId: this.data.beneficiaryId,
					request,
				}),
			);
		}
	}

	updateValidation(): void {
		for (const control in this.form.controls) {
			this.form.get(control)!.markAsTouched();
			this.form.get(control)!.updateValueAndValidity({ onlySelf: true });
		}
	}
}
